import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import {
  getBranches,
  getBranchById,
  createBranch,
  updateBranch,
  deleteBranch,
  getBranchSummary,
} from "services";
import { handleAPIError, queryClient } from "utils";
import { BranchType } from "types";
import { toast } from "react-toastify";
// import { newBranch } from "types";
// import { BranchType } from "types";

// Fetch all branches
export const useGetBranches = () => {
  return useQuery({
    queryKey: [queryKeys.branches],
    queryFn: async () => await getBranches(),
  });
};

// Fetch all branches summary
export const useGetBranchesSummary = () => {
  return useQuery({
    queryKey: [queryKeys.branchesSummary],
    queryFn: async () => await getBranchSummary(),
  });
};

// Fetch a single branch by ID
export const useGetBranchById = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.branches, id], // Unique key for fetching a single branch
    queryFn: async () => await getBranchById(id),
    enabled: !!id, // Only run query if ID is provided
  });
};

// Create a new branch
export const useCreateBranch = () => {
  return useMutation({
    mutationFn: createBranch,
    onSuccess: async () => {
      toast.success("Branch created successful");

      // Invalidate branches and branches summary queries on success to refresh the lists
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes(queryKeys.branches) ||
          query.queryKey.includes(queryKeys.branchesSummary),
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Define the UpdateBranchPayload type
export type UpdateBranchPayload = {
  // Required branch ID
  id: number | string;
  data: BranchType;
};

// Hook to update a branch
export const useUpdateBranch = (): UseMutationResult<
  any,
  Error,
  UpdateBranchPayload
> => {
  return useMutation<any, Error, UpdateBranchPayload>({
    mutationFn: async ({ id, data }) => {
      return updateBranch(id, data);
    },
    onSuccess: async () => {
      // Invalidate branches and branches summary queries on success to refresh the lists
      toast.success("Branch update successful");
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes(queryKeys.branches) ||
          query.queryKey.includes(queryKeys.branchesSummary),
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete a branch
export const useDeleteBranch = () => {
  return useMutation({
    mutationFn: deleteBranch,
    onSuccess: async () => {
      // Invalidate branches and branches summary queries on success to refresh the lists
      toast.success("Branch deleted successfully");

      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes(queryKeys.branches) ||
          query.queryKey.includes(queryKeys.branchesSummary),
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
